import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { BrokerAggregateStats, BrokerMeta, Metric } from '@/types/main';

export type TabState = {
    metricsCache: Record<string, Record<string, Metric>>; // broker -> bucketTs -> Metric
    statsCache: Record<string, BrokerAggregateStats>; // broker -> server-computed aggregate stats
    loading: boolean;
};

// The `yyyy-MM-dd` start/end window handed to the buckets fetch, derived
// from the month (and optional single day) the user picked.
export type DateRange = { start: string; end: string };

export type BrokerMetricsState = {
    brokers: BrokerMeta[];
    endpoints: string[];
    metaLoaded: boolean;
    selectedEndpoint: string;
    comparedBrokers: string[];
    loadingBrokers: Record<string, boolean>;
    tabs: Record<string, TabState>;
    // `selectedMonth` is `yyyy-MM`; `selectedDay` is `yyyy-MM-dd` or '' for
    // "whole month". `dateRange` is the fetch window derived from the two.
    selectedMonth: string;
    selectedDay: string;
    dateRange: DateRange;
};

const initialState: BrokerMetricsState = {
    brokers: [],
    endpoints: [],
    metaLoaded: false,
    selectedEndpoint: '',
    comparedBrokers: [],
    loadingBrokers: {},
    tabs: {},
    selectedMonth: '',
    selectedDay: '',
    dateRange: { start: '', end: '' },
};

function invalidateTabs(state: BrokerMetricsState, exceptEndpoint?: string) {
    Object.keys(state.tabs).forEach((endpoint) => {
        if (endpoint !== exceptEndpoint) {
            state.tabs[endpoint] = { metricsCache: {}, statsCache: {}, loading: false };
        }
    });
}

function ensureTab(state: BrokerMetricsState, endpoint: string): TabState {
    if (!state.tabs[endpoint]) {
        state.tabs[endpoint] = { metricsCache: {}, statsCache: {}, loading: false };
    }
    return state.tabs[endpoint];
}

const brokerMetricsSlice = createSlice({
    name: 'brokerMetrics',
    initialState,
    reducers: {
        // Each broker's color comes from the backend (part of its meta
        // record) - stable per broker, not assigned client-side.
        metaLoaded(state, action: PayloadAction<{ brokers: BrokerMeta[]; endpoints: string[] }>) {
            state.brokers = action.payload.brokers;
            state.endpoints = action.payload.endpoints;
            state.metaLoaded = true;
            if (!state.selectedEndpoint && action.payload.endpoints.length > 0) {
                state.selectedEndpoint = action.payload.endpoints[0];
            }
        },
        initializedFromUrl(state, action: PayloadAction<{ endpoint: string; brokers: string[] }>) {
            state.selectedEndpoint = action.payload.endpoint;
            state.comparedBrokers = action.payload.brokers;
        },
        setSelectedEndpoint(state, action: PayloadAction<string>) {
            state.selectedEndpoint = action.payload;
        },
        // Seeds the default month once; a no-op after the user has picked a
        // month (or a prior seed), so re-initialization can't stomp it.
        monthSelectionInitialized(state, action: PayloadAction<{ month: string; range: DateRange }>) {
            if (!state.selectedMonth) {
                state.selectedMonth = action.payload.month;
                state.selectedDay = '';
                state.dateRange = action.payload.range;
            }
        },
        // A new month/day means every tab's cached buckets are for the wrong
        // window. Inactive tabs are dropped and refetched on next view; the
        // active tab keeps its (now stale) data on screen - dimmed - until
        // the refetch that changeMonthSelection kicks off replaces it, so
        // the chart never blanks to empty mid-change.
        monthSelectionChanged(state, action: PayloadAction<{ month: string; day: string; range: DateRange }>) {
            state.selectedMonth = action.payload.month;
            state.selectedDay = action.payload.day;
            state.dateRange = action.payload.range;
            invalidateTabs(state, state.selectedEndpoint);
        },
        brokerLoadingStarted(state, action: PayloadAction<string>) {
            state.loadingBrokers[action.payload] = true;
        },
        brokerLoadingFinished(state, action: PayloadAction<string>) {
            delete state.loadingBrokers[action.payload];
        },
        brokersLoadingStarted(state, action: PayloadAction<string[]>) {
            action.payload.forEach((broker) => {
                state.loadingBrokers[broker] = true;
            });
        },
        brokersLoadingFinished(state, action: PayloadAction<string[]>) {
            action.payload.forEach((broker) => {
                delete state.loadingBrokers[broker];
            });
        },
        tabLoadingStarted(state, action: PayloadAction<string>) {
            ensureTab(state, action.payload).loading = true;
        },
        tabLoadingFinished(state, action: PayloadAction<string>) {
            ensureTab(state, action.payload).loading = false;
        },
        brokerDataLoaded(
            state,
            action: PayloadAction<{ endpoint: string; broker: string; metricsMap: Record<string, Metric>; stats: BrokerAggregateStats }>
        ) {
            const { endpoint, broker, metricsMap, stats } = action.payload;
            const tab = ensureTab(state, endpoint);
            tab.metricsCache[broker] = metricsMap;
            tab.statsCache[broker] = stats;
            if (!state.comparedBrokers.includes(broker)) {
                state.comparedBrokers.push(broker);
            }
        },
        brokerDataBatchLoaded(
            state,
            action: PayloadAction<{
                endpoint: string;
                results: Record<string, { metricsMap: Record<string, Metric>; stats: BrokerAggregateStats }>;
            }>
        ) {
            const { endpoint, results } = action.payload;
            const tab = ensureTab(state, endpoint);
            Object.entries(results).forEach(([broker, { metricsMap, stats }]) => {
                tab.metricsCache[broker] = metricsMap;
                tab.statsCache[broker] = stats;
                if (!state.comparedBrokers.includes(broker)) {
                    state.comparedBrokers.push(broker);
                }
            });
        },
        brokerRemoved(state, action: PayloadAction<string>) {
            state.comparedBrokers = state.comparedBrokers.filter((broker) => broker !== action.payload);
        },
        brokersRemoved(state, action: PayloadAction<string[]>) {
            const removed = new Set(action.payload);
            state.comparedBrokers = state.comparedBrokers.filter((broker) => !removed.has(broker));
        },
        // Whenever the compared-broker set changes, every OTHER tab's cache
        // is dropped wholesale so it's refetched fresh for the new
        // comparison next time it's viewed - only the active tab is kept
        // (and is updated separately by whatever change triggered this).
        otherTabsInvalidated(state, action: PayloadAction<string>) {
            const activeEndpoint = action.payload;
            Object.keys(state.tabs).forEach((endpoint) => {
                if (endpoint !== activeEndpoint) {
                    state.tabs[endpoint] = { metricsCache: {}, statsCache: {}, loading: false };
                }
            });
        },
    },
});

export const {
    metaLoaded,
    initializedFromUrl,
    setSelectedEndpoint,
    monthSelectionInitialized,
    monthSelectionChanged,
    brokerLoadingStarted,
    brokerLoadingFinished,
    brokersLoadingStarted,
    brokersLoadingFinished,
    tabLoadingStarted,
    tabLoadingFinished,
    brokerDataLoaded,
    brokerDataBatchLoaded,
    brokerRemoved,
    brokersRemoved,
    otherTabsInvalidated,
} = brokerMetricsSlice.actions;

export default brokerMetricsSlice.reducer;
